import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { applyActionCode } from 'firebase/auth';
import { auth } from '../services/firebase';
import AIPiggyBank from '../components/AIPiggyBank';

type VerificationState = 'ready' | 'loading' | 'success' | 'error';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<VerificationState>('ready');
  const [errorMessage, setErrorMessage] = useState('');

  const oobCode = searchParams.get('oobCode');

  const handleVerify = async () => {
    if (!oobCode) {
      setState('error');
      setErrorMessage('Invalid verification link. No verification code found.');
      return;
    }

    setState('loading');

    try {
      await applyActionCode(auth, oobCode);
      setState('success');
    } catch (err: unknown) {
      setState('error');
      if (err instanceof Error) {
        if (err.message.includes('invalid-action-code')) {
          setErrorMessage('This verification link is invalid or has already been used.');
        } else if (err.message.includes('expired-action-code')) {
          setErrorMessage('This verification link has expired. Please request a new one.');
        } else {
          setErrorMessage('Verification failed. The link may have expired or already been used.');
        }
      } else {
        setErrorMessage('An unexpected error occurred. Please try again.');
      }
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #120938 0%, #241A4C 100%)' }}
    >
      <AIPiggyBank className="absolute bottom-0 right-0 w-96 h-96 opacity-25 pointer-events-none select-none transform translate-x-16 translate-y-16" />

      <div className="bg-bg-primary p-8 rounded-2xl shadow-xl w-full max-w-md relative z-10 text-center">
        {/* App Logo */}
        <div className="w-16 h-16 bg-gradient-to-br from-brand-teal to-brand-indigo rounded-2xl mx-auto mb-6 flex items-center justify-center">
          <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>

        {/* Ready State — waiting for user to click */}
        {state === 'ready' && (
          <div className="space-y-4">
            <h1 className="text-2xl font-bold text-text-primary">Verify Your Email</h1>
            <p className="text-text-secondary">
              Tap the button below to confirm your email address for Dough.
            </p>
            <div className="pt-2">
              <button
                onClick={handleVerify}
                className="w-full bg-gradient-to-r from-brand-teal to-brand-indigo text-white py-3 px-6 rounded-xl hover:opacity-90 focus:ring-4 focus:ring-brand-teal/30 transition-all font-semibold text-lg"
              >
                Verify Email
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {state === 'loading' && (
          <div className="space-y-4">
            <div className="flex justify-center">
              <svg className="animate-spin h-12 w-12 text-brand-teal" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-text-primary">Verifying your email...</h1>
            <p className="text-text-secondary">Please wait while we confirm your email address.</p>
          </div>
        )}

        {/* Success State */}
        {state === 'success' && (
          <div className="space-y-4">
            <div className="w-16 h-16 bg-system-success/20 rounded-full mx-auto flex items-center justify-center">
              <svg className="w-8 h-8 text-system-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-text-primary">Email Verified!</h1>
            <p className="text-text-secondary">
              Your email has been successfully verified. You can now close this page and return to the app.
            </p>
            <div className="pt-4">
              <p className="text-sm text-text-tertiary">
                You may now sign in with your verified email address.
              </p>
            </div>
          </div>
        )}

        {/* Error State */}
        {state === 'error' && (
          <div className="space-y-4">
            <div className="w-16 h-16 bg-system-error/20 rounded-full mx-auto flex items-center justify-center">
              <svg className="w-8 h-8 text-system-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-text-primary">Verification Failed</h1>
            <p className="text-text-secondary">{errorMessage}</p>
            <div className="pt-4 space-y-3">
              <p className="text-sm text-text-tertiary">
                Need a new verification email? Open the app and request a new verification link from your account settings.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
